

import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  X,
  Puzzle,
} from 'lucide-react';
import { api, Feature, ApiError, FeatureCategory } from '@/lib/api';

const categoryColors: Record<string, { bg: string; text: string }> = {
  content: { bg: 'bg-blue-50', text: 'text-blue-700' },
  engagement: { bg: 'bg-purple-50', text: 'text-purple-700' },
  analytics: { bg: 'bg-amber-50', text: 'text-amber-700' },
  support: { bg: 'bg-green-50', text: 'text-green-700' },
  customization: { bg: 'bg-pink-50', text: 'text-pink-700' },
  general: { bg: 'bg-gray-50', text: 'text-gray-700' },
};

export default function FeaturesPage() {
  const [search, setSearch] = React.useState('');
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [features, setFeatures] = React.useState<Feature[]>([]);
  const [categories, setCategories] = React.useState<FeatureCategory[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null);

  const loadData = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [featuresData, categoriesData] = await Promise.all([
        api.getFeatures(includeInactive),
        api.getFeatureCategories(),
      ]);
      setFeatures(featuresData);
      setCategories(categoriesData);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao carregar recursos');
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (id: string, name: string) => {
    if (
      !confirm(
        `Tem certeza que deseja excluir o recurso "${name}"? Esta acao nao pode ser desfeita.`
      )
    ) {
      return;
    }

    try {
      setIsDeleting(id);
      await api.deleteFeature(id);
      setFeatures((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir recurso');
    } finally {
      setIsDeleting(null);
    }
  };

  const filteredFeatures = features.filter(
    (feature) =>
      feature.name.toLowerCase().includes(search.toLowerCase()) ||
      feature.key.toLowerCase().includes(search.toLowerCase())
  );

  const getCategoryLabel = (categoryValue: string) => {
    const cat = categories.find((c) => c.value === categoryValue);
    return cat?.label || categoryValue;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Recursos</h1>
          <p className="text-gray-500 mt-1">
            Gerencie os recursos disponiveis para os planos
          </p>
        </div>
        <Link
          to="/features/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25"
        >
          <Plus className="h-5 w-5" />
          Novo Recurso
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar funcionalidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-12 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#0057FF] focus:ring-[#0057FF]"
            />
            <span className="text-sm text-gray-600">Incluir inativas</span>
          </label>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
            <p className="text-gray-500">Carregando recursos...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">
                Erro ao carregar recursos
              </h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button
              onClick={loadData}
              className="px-4 py-2 rounded-xl bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {/* Features Table */}
      {!isLoading && !error && (
        <>
          {filteredFeatures.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
              <div className="flex flex-col items-center gap-4">
                <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                  <Puzzle className="h-8 w-8 text-gray-400" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-900">
                    Nenhum recurso encontrado
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {search
                      ? 'Tente buscar com outros termos'
                      : 'Comece criando seu primeiro recurso'}
                  </p>
                </div>
                {!search && (
                  <Link
                    to="/features/new"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0057FF] text-white text-sm font-medium hover:bg-[#0057FF]/90 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Novo Recurso
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Key
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Ordem
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Acoes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFeatures.map((feature) => {
                    const colorConfig = categoryColors[feature.category] || categoryColors.general;
                    return (
                      <tr
                        key={feature.id}
                        className={`hover:bg-gray-50 transition-colors ${
                          !feature.isActive ? 'opacity-60' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{feature.name}</p>
                            {feature.description && (
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                                {feature.description}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                            {feature.key}
                          </code>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${colorConfig.bg} ${colorConfig.text}`}
                          >
                            {getCategoryLabel(feature.category)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm text-gray-600">
                            {feature.displayOrder}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              feature.isActive
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {feature.isActive ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                            {feature.isActive ? 'Ativa' : 'Inativa'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/features/${feature.id}/edit`}
                              className="p-2 rounded-lg text-gray-400 hover:text-[#0057FF] hover:bg-[#0057FF]/10 transition-colors"
                            >
                              <Pencil className="h-5 w-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(feature.id, feature.name)}
                              disabled={isDeleting === feature.id}
                              className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isDeleting === feature.id ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                              ) : (
                                <Trash2 className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary */}
          {filteredFeatures.length > 0 && (
            <div className="text-sm text-gray-500 text-center">
              Mostrando{' '}
              <span className="font-medium text-gray-900">
                {filteredFeatures.length}
              </span>{' '}
              de{' '}
              <span className="font-medium text-gray-900">{features.length}</span>{' '}
              recursos
            </div>
          )}
        </>
      )}
    </div>
  );
}
